/**
 * USE SUPPORTED COUNTRIES HOOK
 *
 * Database-backed hook for fetching countries from the supported_countries table.
 * Falls back to hardcoded completeCountryList when database is empty or errors.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../supabase';
import { COMPLETE_COUNTRY_LIST, CompleteCountry } from '../data/completeCountryList';

export interface SupportedCountry {
 id: number;
 country_code: string;
 country_name: string;
 flag_emoji: string;
 is_primary: boolean;
 is_active: boolean;
 display_order: number;
 region: string;
}

// Convert hardcoded list to SupportedCountry format for fallback
const FALLBACK_COUNTRIES: SupportedCountry[] = COMPLETE_COUNTRY_LIST.map((c: CompleteCountry, index: number) => ({
 id: index + 1,
 country_code: c.code,
 country_name: c.name,
 flag_emoji: c.flag,
 is_primary: ['ZAF', 'NAM', 'SWZ', 'BWA', 'GBR', 'USA'].includes(c.code),
 is_active: true,
 display_order: index + 1,
 region: c.region
}));

export interface CountryOption {
 value: string; // country_code
 label: string; // country_name
 flag: string; // flag_emoji
 isPrimary: boolean;
}

interface UseSupportedCountriesOptions {
 includeInactive?: boolean;
 searchQuery?: string;
 prioritizeCountries?: string[]; // Country codes to show at top
}

interface UseSupportedCountriesResult {
 countries: SupportedCountry[];
 countryOptions: CountryOption[];
 loading: boolean;
 error: string | null;
 getCountryByCode: (code: string) => SupportedCountry | undefined;
 getCountryName: (code: string) => string;
 searchCountries: (query: string) => SupportedCountry[];
 refetch: () => Promise<void>;
}

// Cache for countries to avoid repeated fetches
let countriesCache: SupportedCountry[] | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function useSupportedCountries(
 options: UseSupportedCountriesOptions = {}
): UseSupportedCountriesResult {
 const { includeInactive = false, searchQuery = '', prioritizeCountries = [] } = options;
 
 const [countries, setCountries] = useState<SupportedCountry[]>([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);

 const fetchCountries = useCallback(async () => {
 // Check cache first
 const now = Date.now();
 if (countriesCache && (now - cacheTimestamp) < CACHE_DURATION) {
 setCountries(countriesCache);
 setLoading(false);
 return;
 }

 setLoading(true);
 setError(null);

 try {
 let query = supabase
 .from('supported_countries')
 .select('*')
 .order('is_primary', { ascending: false })
 .order('display_order', { ascending: true })
 .order('country_name', { ascending: true })
 .range(0, 299); // v2.9.89: Explicitly fetch all rows (table has ~200 countries)

 if (!includeInactive) {
 query = query.eq('is_active', true);
 }

 const { data, error: fetchError } = await query;

 if (fetchError) throw fetchError;

 let fetchedCountries = data || [];

 // FALLBACK: If database returns empty, use hardcoded list
 if (fetchedCountries.length === 0) {
 console.warn('supported_countries table is empty - using fallback country list');
 countriesCache = FALLBACK_COUNTRIES;
 cacheTimestamp = now;
 setCountries(FALLBACK_COUNTRIES);
 return;
 }

 // v2.9.89 SAFETY NET: Ensure critical priority countries are always present
 const PRIORITY_CODES = ['ZAF', 'NAM', 'SWZ', 'BWA', 'GBR', 'USA'];
 const fetchedCodes = new Set(fetchedCountries.map((c: any) => c.country_code));
 const missingPriority = PRIORITY_CODES.filter(code => !fetchedCodes.has(code));
 if (missingPriority.length > 0) {
 console.warn('Missing priority countries from DB fetch, merging from fallback:', missingPriority);
 const fallbackAdditions = FALLBACK_COUNTRIES.filter((c: any) => missingPriority.includes(c.country_code));
 fetchedCountries = [...fallbackAdditions, ...fetchedCountries];
 }

 // Update cache
 countriesCache = fetchedCountries;
 cacheTimestamp = now;

 setCountries(fetchedCountries);
 } catch (err) {
 console.error('Error fetching supported countries:', err);
 setError(err instanceof Error ? err.message : 'Failed to load countries');

 // Fallback to cache if available, otherwise use hardcoded list
 if (countriesCache && countriesCache.length > 0) {
 setCountries(countriesCache);
 } else {
 console.warn('Using fallback country list due to database error');
 setCountries(FALLBACK_COUNTRIES);
 }
 } finally {
 setLoading(false);
 }
 }, [includeInactive]);

 useEffect(() => {
 fetchCountries();
 }, [fetchCountries]);

 // Filter and sort countries based on options
 const filteredCountries = useMemo(() => {
 let result = [...countries];

 // Apply search filter
 if (searchQuery) {
 const query = searchQuery.toLowerCase();
 result = result.filter(c => 
 c.country_name.toLowerCase().includes(query) ||
 c.country_code.toLowerCase().includes(query)
 );
 }

 // Apply priority ordering if specified
 if (prioritizeCountries.length > 0) {
 result.sort((a, b) => {
 const aIndex = prioritizeCountries.indexOf(a.country_code);
 const bIndex = prioritizeCountries.indexOf(b.country_code);
 
 if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
 if (aIndex !== -1) return -1;
 if (bIndex !== -1) return 1;
 
 // Keep existing order for non-prioritized
 return 0;
 });
 }

 return result;
 }, [countries, searchQuery, prioritizeCountries]);

 // Convert to options format for dropdowns
 const countryOptions = useMemo((): CountryOption[] => {
 return filteredCountries.map(c => ({
 value: c.country_code,
 label: c.country_name,
 flag: c.flag_emoji,
 isPrimary: c.is_primary
 }));
 }, [filteredCountries]);

 const getCountryByCode = useCallback((code: string): SupportedCountry | undefined => {
 return countries.find(c => c.country_code === code);
 }, [countries]);

 const getCountryName = useCallback((code: string): string => {
 const country = countries.find(c => c.country_code === code);
 return country?.country_name || code;
 }, [countries]);

 const searchCountriesFunc = useCallback((query: string): SupportedCountry[] => {
 if (!query) return countries;
 const q = query.toLowerCase();
 return countries.filter(c =>
 c.country_name.toLowerCase().includes(q) ||
 c.country_code.toLowerCase().includes(q)
 );
 }, [countries]);

 return {
 countries: filteredCountries,
 countryOptions,
 loading,
 error,
 getCountryByCode,
 getCountryName,
 searchCountries: searchCountriesFunc,
 refetch: fetchCountries
 };
}

/**
 * Utility to clear the countries cache (call when admin updates countries)
 */
export function clearCountriesCache(): void {
 countriesCache = null;
 cacheTimestamp = 0;
}

/**
 * Hook for fetching a single country by code
 */
export function useCountryByCode(code: string | null | undefined) {
 const { countries, loading, error } = useSupportedCountries();

 const country = useMemo(() => {
 if (!code) return null;
 return countries.find(c => c.country_code === code) || null;
 }, [countries, code]);

 return { country, loading, error };
}

